import { H2HMatchRow } from "@components/StatsConsole/Widgets/H2HWidget/parts/H2H/H2HMatchRow";
import { useTranslations } from "../../../../../../translation/TranslationContext";
import { useLanguage } from "@utils/hooks/useLanguage";
import RefreshIconAndText from "@components/common/RefreshIconAndText";

interface KnockoutProps {
    knockoutData: any;
}

interface Match {
    home_team: string;
    away_team: string;
    home_score: number | null;
    away_score: number | null;
    status: string;
    date: string;
    is_current_match: boolean;
    winner: string | null;
    is_aggregate: boolean;
    agg_away_score: number | null;
    agg_home_score: number | null;
    home_team_inPlay: boolean;
    away_team_inPlay: boolean;
    penalty_home_score?: number | null;
    penalty_away_score?: number | null;
    decided_by_penalties?: boolean;
    penalty_winner?: string | null;
}

interface Stage {
    stage_name: string;
    label: string;
    is_current_stage: boolean;
    matches: Match[];
    match_progress: {
        completed_matches: number;
        total_matches: number;
        is_multi_leg: boolean;
        progress_text: string;
    };
}

const Knockout = ({ knockoutData }: KnockoutProps) => {
    const language = useLanguage();
    const t = useTranslations(language);

    const hasStages = knockoutData?.stages && Array.isArray(knockoutData.stages);


    if (!hasStages) {
        return (
            <RefreshIconAndText addClass="pt-[100px]" />
        );
    }

    const getStageDisplayName = (stageName: string, stageLabel: string): string => {
        const stageNameMap: { [key: string]: string } = {
            '3rd_place_final': t['InHouseStats.ThirdPlacePlayoff'] || 'Third-place playoff',
            '3rd_place_playoff': t['InHouseStats.ThirdPlacePlayoff'] || 'Third-place playoff',
            'east_wild_card': (t as Record<string, string>)['InHouseStats.EastWildCard'] || 'East Wild Card',
            'eastern_conference_finals': (t as Record<string, string>)['InHouseStats.EasternConferenceFinals'] || 'Eastern Conference finals',
            'eastern_conference_semifinals': (t as Record<string, string>)['InHouseStats.EasternConferenceSemifinals'] || 'Eastern Conference semi-finals',
            'final': t['InHouseStats.Final'] || 'Final',
            'mls_cup': (t as Record<string, string>)['InHouseStats.MLSCup'] || 'MLS Cup',
            'playoff_round': t['InHouseStats.Playoffs'] || 'Playoffs',
            'qualification_round_1': (t as Record<string, string>)['InHouseStats.QualificationRound1'] || 'Qualifying round 1',
            'qualification_round_2': (t as Record<string, string>)['InHouseStats.QualificationRound2'] || 'Qualifying round 2',
            'qualification_round_3': (t as Record<string, string>)['InHouseStats.QualificationRound3'] || 'Qualifying round 3',
            'quarterfinal': t['InHouseStats.QuarterFinals'] || 'Quarter-finals',
            'round_1': t['InHouseStats.Round1'] || 'Round 1',
            'round_2': t['InHouseStats.Round2'] || 'Round 2',
            'round_3': t['InHouseStats.Round3'] || 'Round 3',
            'round_4': t['InHouseStats.Round4'] || 'Round 4',
            'round_5': t['InHouseStats.Round5'] || 'Round 5',
            'round_7': (t as Record<string, string>)['InHouseStats.Round7'] || 'Round 7',
            'round_8': 'Round 8',
            'round_of_16': t['InHouseStats.RoundOf16'] || 'Round of 16',
            'round_of_32': t['InHouseStats.RoundOf32'] || 'Round of 32',
            'round_of_64': t['InHouseStats.RoundOf64'] || 'Round of 64',
            'semifinal': t['InHouseStats.SemiFinals'] || 'Semi-finals',
            'west_wild_card': (t as Record<string, string>)['InHouseStats.WestWildCard'] || 'West Wild Card',
            'western_conference_finals': (t as Record<string, string>)['InHouseStats.WesternConferenceFinals'] || 'Western Conference finals',
            'western_conference_semifinals': (t as Record<string, string>)['InHouseStats.WesternConferenceSemifinals'] || 'Western Conference semi-finals',
        };
        if (stageLabel) {
            return (t as Record<string, string>)[stageLabel] || stageLabel;
        } else {
            return stageNameMap[stageName] || stageName;
        }
    };

    const renderMatch = (match: Match, index: number, isMultiLeg: boolean) => {
        const isMatchPlayed = match.home_score !== null && match.away_score !== null;
        let matchLabel = "";
        if (isMatchPlayed && isMultiLeg) {
            if (match.is_aggregate) {
                matchLabel = t['InHouseStats.Aggregate'];
                matchLabel = matchLabel + ` ${match.agg_home_score}-${match.agg_away_score}`;
            } else {
                matchLabel = t['InHouseStats.Leg1'];
            }
        }

        let displayHomeScore = match.home_score;
        let displayAwayScore = match.away_score;
        let showPenaltyScores = false;
        
        if (match.decided_by_penalties && match.penalty_home_score !== null && match.penalty_away_score !== null) {
            showPenaltyScores = true;
        }
        
        const isMatchEnded = match.status === 'ended';
        const homeTeamEliminated = isMatchEnded && match.winner !== null && match.winner !== match.home_team;
        const awayTeamEliminated = isMatchEnded && match.winner !== null && match.winner !== match.away_team;
        
        return (
            <H2HMatchRow
                key={`match-${index}`}
                date={match.date}
                league={matchLabel}
                homeTeam={match.home_team}
                homeScore={displayHomeScore || 0}
                awayScore={displayAwayScore || 0}
                awayTeam={match.away_team}
                status={null}
                isLatestMatch={match.is_current_match}
                homeTeamInPlay={match.home_team_inPlay}
                awayTeamInPlay={match.away_team_inPlay}
                showPenaltyScores={showPenaltyScores}
                penaltyHomeScore={match.penalty_home_score}
                penaltyAwayScore={match.penalty_away_score}
                matchStatus={match.status}
                homeTeamEliminated={homeTeamEliminated}
                awayTeamEliminated={awayTeamEliminated}
            />
        );
    };

    const renderStage = (stage: Stage, stageIndex: number) => {
        if (!stage.matches || stage.matches.length === 0) {
            return null;
        }

        return (
            <div key={`stage-${stageIndex}`} className="w-full mb-6">
                <div className="mb-4">
                    <div className="flex flex-row justify-between items-center mb-2">
                        <div className="text-[12px] leading-[16px] font-robotoMedium font-medium text-highlight-light dark:text-highlight-dark">
                            {getStageDisplayName(stage.stage_name, stage.label)}
                        </div>
                    </div>
                </div>
                <div className="w-full">
                    {stage.matches.map((match, matchIndex) => renderMatch(match, matchIndex, stage.match_progress.is_multi_leg))}
                </div>
            </div>
        );
    };


    return (
        <div data-testid="knockout-container" className="sm351:w-[319px] w-full">
            <div className="w-full flex justify-center items-center text-[12px] leading-[16px] font-robotoMedium font-medium text-monoX dark:text-monoY mt-[20px] mb-[24px]">{knockoutData.leagueName}</div>
            {knockoutData.stages.map((stage: Stage, index: number) => renderStage(stage, index))}
        </div>
    );
};

export default Knockout;
